import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, Clock, Users, Loader2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { api } from '../lib/api';

interface DayJob {
  id: string;
  job_number?: string;
  title: string;
  time?: string;
  duration_type: string;
  duration_hours?: number;
  duration_days?: number;
  assigned_to: string;
  status: string;
  client_name?: string;
}

const WORKERS = ['Sammy', 'Jim', 'Stewart'];
const HOUR_START = 7; // 07:00
const HOUR_END = 19; // 19:00
const TOTAL_HOURS = HOUR_END - HOUR_START;

// Return start-hour and end-hour (float, 24h) for a job
function jobBounds(job: DayJob): [number, number] | null {
  if (!job.time) return null;
  const [hh, mm] = job.time.split(':').map(Number);
  const startH = hh + (mm || 0) / 60;
  let durationH = 1;
  if (job.duration_type === 'hours' && job.duration_hours) {
    durationH = job.duration_hours;
  } else if (job.duration_type === 'days' && job.duration_days) {
    durationH = job.duration_days * 8; // treat 1 day = 8h
  }
  return [startH, startH + durationH];
}

function overlaps(a: [number, number], b: [number, number]) {
  return a[0] < b[1] && b[0] < a[1];
}

export default function ScheduleCheck({
  date,
  time,
  durationType,
  durationHours,
  durationDays,
  assignedTo,
  excludeJobId,
  onAssignChange,
}: {
  date: string;
  time: string;
  durationType: 'hours' | 'days';
  durationHours: number;
  durationDays: number;
  assignedTo: string;
  excludeJobId?: string;
  onAssignChange?: (worker: string) => void;
}) {
  const [jobs, setJobs] = useState<DayJob[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!date) return;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/jobs/date/${date}`);
        setJobs((res.data || []).filter((j: DayJob) => j.id !== excludeJobId));
      } catch (e) {
        setJobs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [date, excludeJobId]);

  // Bounds of the new job
  const newJobBounds: [number, number] | null = useMemo(() => {
    if (!time) return null;
    const [hh, mm] = time.split(':').map(Number);
    const startH = hh + (mm || 0) / 60;
    const durH =
      durationType === 'hours' ? durationHours : (durationDays || 1) * 8;
    return [startH, startH + durH];
  }, [time, durationType, durationHours, durationDays]);

  // Conflict detection for the assigned worker
  const conflicts = useMemo(() => {
    if (!newJobBounds || !assignedTo) return [];
    return jobs.filter((j) => {
      if (j.assigned_to?.toLowerCase() !== assignedTo.toLowerCase()) return false;
      const b = jobBounds(j);
      if (!b) return false;
      return overlaps(newJobBounds, b);
    });
  }, [jobs, newJobBounds, assignedTo]);

  // Group jobs by worker
  const jobsByWorker = useMemo(() => {
    const map: Record<string, DayJob[]> = {};
    WORKERS.forEach((w) => (map[w] = []));
    jobs.forEach((j) => {
      const key = WORKERS.find((w) => w.toLowerCase() === j.assigned_to?.toLowerCase());
      if (key) map[key].push(j);
    });
    return map;
  }, [jobs]);

  const hasNewJob = !!newJobBounds;

  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-slate-500" />
          <div className="font-semibold text-slate-900 text-sm">
            Team schedule · {date ? format(parseISO(date), 'EEE d MMM') : '—'}
          </div>
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
      </div>

      {/* Conflict banner */}
      {conflicts.length > 0 ? (
        <div className="flex items-start gap-2 px-4 py-2.5 bg-red-50 border-b border-red-200">
          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
          <div className="text-xs">
            <div className="font-bold text-red-800">
              {conflicts.length} conflict{conflicts.length > 1 ? 's' : ''} with {assignedTo}'s schedule
            </div>
            <div className="text-red-700 mt-0.5">
              {conflicts
                .map((c) => `${c.job_number || c.title} at ${c.time}`)
                .join(', ')}
            </div>
          </div>
        </div>
      ) : hasNewJob && assignedTo ? (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 border-b border-emerald-200">
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
          <span className="text-xs font-semibold text-emerald-800">
            {assignedTo} is free at {time}. No conflict.
          </span>
        </div>
      ) : null}

      {/* Timeline */}
      <div className="p-4 space-y-2 overflow-x-auto">
        {/* Hour ruler */}
        <div className="relative h-4 min-w-[600px]">
          <div className="flex text-[10px] font-semibold text-slate-400 tabular-nums">
            {Array.from({ length: TOTAL_HOURS + 1 }).map((_, i) => (
              <div
                key={i}
                className="flex-1"
                style={{ minWidth: 42 }}
              >
                {String(HOUR_START + i).padStart(2, '0')}:00
              </div>
            ))}
          </div>
        </div>

        {WORKERS.map((worker) => {
          const workerJobs = jobsByWorker[worker] || [];
          const isTarget = worker.toLowerCase() === assignedTo?.toLowerCase();
          const hasConflict = conflicts.some(
            (c) => c.assigned_to?.toLowerCase() === worker.toLowerCase()
          );
          return (
            <div key={worker} className="flex items-center gap-2 min-w-[600px]">
              <button
                type="button"
                onClick={() => onAssignChange?.(worker)}
                disabled={!onAssignChange}
                className={`w-20 shrink-0 text-xs font-semibold flex items-center gap-1 px-2 py-1 rounded-md transition ${
                  onAssignChange ? 'hover:bg-slate-100 cursor-pointer' : 'cursor-default'
                } ${isTarget ? 'text-brand-800 font-bold' : 'text-slate-500'}`}
                title={onAssignChange ? `Assign to ${worker}` : ''}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full ${
                    isTarget ? 'bg-brand-600' : 'bg-slate-300'
                  }`}
                />
                <span className="truncate">{worker}</span>
              </button>
              <div
                onClick={() => onAssignChange?.(worker)}
                className={`relative flex-1 h-9 rounded-lg border transition ${
                  isTarget
                    ? 'bg-brand-50/30 border-brand-100'
                    : `bg-slate-50 border-slate-100 ${onAssignChange ? 'cursor-pointer hover:bg-slate-100/60' : ''}`
                }`}
              >
                {/* Existing jobs */}
                {workerJobs.map((j) => {
                  const b = jobBounds(j);
                  if (!b) return null;
                  const startPct = Math.max(0, ((b[0] - HOUR_START) / TOTAL_HOURS) * 100);
                  const widthPct = Math.min(
                    100 - startPct,
                    ((b[1] - b[0]) / TOTAL_HOURS) * 100
                  );
                  if (widthPct <= 0) return null;
                  const isConflict = conflicts.some((c) => c.id === j.id);
                  return (
                    <Link
                      key={j.id}
                      to={`/jobs/${j.id}`}
                      title={`${j.job_number || ''} ${j.title} · ${j.time}`}
                      className={`absolute inset-y-1 rounded-md px-2 flex items-center text-[11px] font-semibold overflow-hidden ${
                        isConflict
                          ? 'bg-red-500 text-white ring-2 ring-red-300'
                          : 'bg-slate-700 text-white hover:bg-slate-800'
                      }`}
                      style={{
                        left: `${startPct}%`,
                        width: `${widthPct}%`,
                      }}
                    >
                      <span className="truncate">{j.job_number || j.title}</span>
                    </Link>
                  );
                })}

                {/* New job ghost */}
                {isTarget && newJobBounds && (
                  <div
                    className={`absolute inset-y-0 rounded-md border-2 border-dashed pointer-events-none ${
                      hasConflict
                        ? 'border-red-500 bg-red-500/20'
                        : 'border-brand-600 bg-brand-500/20'
                    }`}
                    style={{
                      left: `${Math.max(0, ((newJobBounds[0] - HOUR_START) / TOTAL_HOURS) * 100)}%`,
                      width: `${Math.min(
                        100 - Math.max(0, ((newJobBounds[0] - HOUR_START) / TOTAL_HOURS) * 100),
                        ((newJobBounds[1] - newJobBounds[0]) / TOTAL_HOURS) * 100
                      )}%`,
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}

        {jobs.length === 0 && !loading && (
          <p className="text-xs text-slate-400 italic">
            Nothing else booked for this day.
          </p>
        )}
      </div>

      <div className="px-4 py-2 border-t border-slate-100 bg-slate-50/50 flex items-center gap-4 text-[10px] font-medium text-slate-500">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded-sm bg-slate-700" /> Existing
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded-sm border-2 border-dashed border-brand-600" /> New job
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-2 rounded-sm bg-red-500" /> Conflict
        </span>
        <Clock className="h-3 w-3 ml-auto" />
      </div>
    </div>
  );
}
